import Styles from './WizardsDrawerV4.module.scss'
import { Drawer } from 'components/Drawer'
import { ActionsProps } from '../../Types'
import { DrawerProps } from 'components/Drawer/Root'
import { observer } from 'mobx-react-lite'
import { HelpDropdown } from 'blaze-components/TopBar/HelpDropdown/HelpDropdown'
import { Button } from 'blaze-ui/Button'
import {
  Facebook,
  FolderPlus2,
  Help,
  Instagram,
  LinkedIn,
  Pinterest,
  TikTok,
  Twitter,
  WhatsApp,
  YouTube,
} from 'blaze-ui/icons'
import { CreateNew } from './CreateNew/CreateNew'
import { Level } from './Types'
import { useRef, useState } from 'react'
import { useRootStore } from 'stores/RootStore'
import { useTouchSwipe } from 'hooks/useTouchSwipe'
import { useIsScrolled } from 'hooks/useIsScrolled'
import cx from 'classnames'
import { NewFileDrawer } from '../NewFileDrawer'
import { NewProjectDrawer } from '../NewProjectDrawer'
import { Icon } from 'blaze-ui/icons/Types'

interface Props extends ActionsProps, DrawerProps {}

export const LEVEL_TITLES: Partial<Record<Level, { title: string; icon?: Icon }>> = {
  instagram: {
    title: 'Instagram',
    icon: Instagram,
  },
  facebook: {
    title: 'Facebook',
    icon: Facebook,
  },
  linkedin: {
    title: 'LinkedIn',
    icon: LinkedIn,
  },
  tiktok: {
    title: 'TikTok',
    icon: TikTok,
  },
  twitter: {
    title: 'X/Twitter',
    icon: Twitter,
  },
  youtube: {
    title: 'YouTube',
    icon: YouTube,
  },
  pinterest: {
    title: 'Pinterest',
    icon: Pinterest,
  },
  whatsapp: {
    title: 'WhatsApp',
    icon: WhatsApp,
  },
  product_copy: { title: 'Product Copy' },
  product_visual: { title: 'Product Visual' },
  blogs_seo: { title: 'Blog/SEO' },
  brainstorm_ideas: { title: 'Brainstorm Ideas' },
  ad_copy: { title: 'Ad Copy' },
  newsletter: { title: 'Newsletter' },
  lifecycle: { title: 'Lifecycle' },
  ecommerce: { title: 'Ecommerce' },
  email_sales: { title: 'Sales Outbound' },
  websites: { title: 'Websites' },
  sales: { title: 'Sales' },
  customer_support: { title: 'Customer Support' },
  recruiting: { title: 'Recruiting' },
  linkedin_post: { title: 'LinkedIn Post', icon: LinkedIn },
}

export const WizardsDrawerV4 = observer(({ initialValues, allowNewProject = true, onSuccess, ...props }: Props) => {
  const { drawersStore } = useRootStore()
  const containerRef = useRef<HTMLDivElement>(null)
  const isScrolled = useIsScrolled(containerRef.current)
  const [scrollPositions, setScrollPositions] = useState<Record<string, number>>({})
  const [backStack, setBackStack] = useState<Level[]>([])
  const [level, setLevel] = useState<Level>(initialValues?.level ?? 'create_new')
  const { title: headerTitle, icon: HeaderIcon } = LEVEL_TITLES[level] ?? {}

  const handleBack = () => {
    if (backStack.length === 0) {
      drawersStore.closeAllDrawers()
      return
    }

    const newBackStack = [...backStack]
    const newLevel = newBackStack.pop() as Level
    const scrollPosition = scrollPositions[newLevel] ?? 0
    const newScrollPositions = { ...scrollPositions }
    delete newScrollPositions[newLevel]

    setScrollPositions(newScrollPositions)
    setBackStack(newBackStack)
    setLevel(newLevel)

    // Reset previous scroll position when navigating back
    setTimeout(() => {
      if (scrollPosition > 0 && containerRef.current) {
        containerRef.current.scrollTop = scrollPosition
      }
    })
  }

  const pushToBackStack = (l: Level) => {
    if (backStack.includes(l)) {
      return
    }

    const scrollTop = containerRef.current?.scrollTop ?? 0
    setScrollPositions({ ...scrollPositions, [l]: scrollTop })
    setBackStack([...backStack, l])
  }

  const handleDocument = (type: 'document' | 'design') => {
    drawersStore.openDrawer(NewFileDrawer, {
      initialFileType: type,
      onSuccess: () => drawersStore.closeAllDrawers(),
    })
  }

  const setLevelWithBack = (l: Level) => {
    if (l === 'new_document') {
      handleDocument('document')
      return
    }

    if (l === 'new_design') {
      handleDocument('design')
      return
    }

    pushToBackStack(level ?? 'create_new')

    if (containerRef.current) {
      containerRef.current.scrollTop = 0
    }

    setLevel(l)
  }

  useTouchSwipe({ containerRef, handleSwipe: handleBack, direction: 'left' })

  // For convenient function generation for event handlers on nested screens
  const handleLevel = (l: Level) => () => {
    setLevelWithBack(l)
  }

  const handleNewProject = () => {
    drawersStore.openDrawer(NewProjectDrawer, {
      onSuccess: () => drawersStore.closeAllDrawers(),
    })
  }

  return (
    <Drawer.Root {...props}>
      <Drawer.Portal>
        <Drawer.Content fullHeight>
          <Drawer.InnerContent isSnappedTop color="var(--light-100)">
            <Drawer.Top className={cx({ [Styles.scrolled]: isScrolled })}>
              <Drawer.Back onPress={handleBack} />
              <h5 className={Styles.headerTitle}>
                {HeaderIcon && <HeaderIcon size={20} />}
                <span>{headerTitle}</span>
              </h5>
              <div className={Styles.topButtons}>
                <Button
                  data-testid="new-project-button"
                  aria-label="New Project"
                  variant="tertiary"
                  size="md"
                  frontIcon={FolderPlus2}
                  rounded
                  onPress={handleNewProject}
                />
                <HelpDropdown
                  trigger={<Button aria-label="Help" variant="tertiary" size="md" frontIcon={Help} rounded />}
                />
                {/* <Button variant="tertiary" size="md" frontIcon={Search} rounded /> */}
              </div>
            </Drawer.Top>
            <Drawer.Container ref={containerRef} padding="0px" className={Styles.container}>
              <CreateNew level={level} setLevel={setLevelWithBack} handleLevel={handleLevel} back={handleBack} />
            </Drawer.Container>
          </Drawer.InnerContent>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
})
